import { useCallback, useState } from 'react';

import { errorMessageFromResponse } from '../helpers/errors';
import type { FailedFetchResponse } from '../helpers/errors';
import type { AsyncStatus } from '../types';

export type UseAsyncStatus = {
  status: AsyncStatus;
  onPending: () => void;
  onSuccess: () => void;
  // onError extracts and stores an error message, sets `loading` to false, and returns the extracted message.
  onError: (failedResponse: FailedFetchResponse) => string;
  clearError: () => void;
};

/**
 * Hook for managing AsyncStatus. Useful in parts of the app where we don't
 * use Redux, but still want to follow the same patterns for loading/errors.
 *
 * @deprecated - Prefer the `useQuery`/`useMutation` hooks from `./app/ReactQuery/` instead. Do not add new usages.
 */
export default function useAsyncStatus(isLoading = false): UseAsyncStatus {
  const [status, setState] = useState<AsyncStatus>({
    isLoading,
    errors: null,
    // @deprecated, don't use these anymore.
    loading: isLoading,
    error: null,
  });

  const onPending = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isLoading: true,
      errors: null,
      // @deprecated, don't use these anymore.
      loading: true,
      error: null,
    }));
  }, []);

  const onSuccess = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      isLoading: false,
      // @deprecated, don't use these anymore.
      loading: false,
    }));
  }, []);

  const onError = useCallback((failedResponse: FailedFetchResponse) => {
    const errorMessage = errorMessageFromResponse(failedResponse);
    setState(prevState => ({
      ...prevState,
      isLoading: false,
      errors: [errorMessage],
      // @deprecated, don't use these anymore.
      loading: false,
      error: errorMessage,
    }));

    return errorMessage;
  }, []);

  const clearError = useCallback(() => {
    setState(prevState => ({
      ...prevState,
      errors: null,
      // @deprecated, don't use these anymore.
      error: null,
    }));
  }, []);

  return {
    status,
    onPending,
    onSuccess,
    onError,
    clearError,
  };
}
